/* global Messenger */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FocusTrap } from 'focus-trap-react';

import { uiConfig } from '../../../../conf/criteria';

// Context
import { CriteriaStoreContext } from '../../../state/criteria/context';

// Components
import Form from '../../../../../components/Form';
import FeedbackCard from '../../../../../components/FeedbackCard';
import { Dialog } from '../../../../../components/Dialog';
import { DialogHeader } from '../../../../../components/DialogHeader';
import { DialogBody } from '../../../../../components/DialogBody';
import { DialogFooter } from '../../../../../components/DialogFooter';
import { Button } from '../../../../../components/Button';
import { Spinner } from '../../../../../components/Spinner';
import { MaterialIcon } from '../../../../../components/MaterialIcon';
import TabbedContainer from '../../../../../components/TabbedContainer';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import { RuleEditor } from './components/RuleEditor';

// Enums
import { dtoState } from '../../../../../common/enums/dtoState';

// Hooks
import { useCreateCriteria } from '../../../api/hooks/useCreateCriteria';
import { useSaveCriteria } from '../../../api/hooks/useSaveCriteria';
import { useGetCriteriaById } from '../../../api/hooks/useGetCriteriaById';
import { useGetCriteriaMetadata } from '../../../api/hooks/useGetCriteriaMetadata';
import { useCriteriaStoreBuildDTO } from '../../../state/criteria/hooks/useCriteriaStoreBuildDTO';
import { useCriteriaStoreInit } from '../../../state/criteria/hooks/useCriteriaStoreInit';
import { useCriteriaStoreReset } from '../../../state/criteria/hooks/useCriteriaStoreReset';
import { useCriteriaStorePredicate } from '../../../state/criteria/hooks/useCriteriaStorePredicate';

// Store
import { createCriteriaStore } from '../../../state/criteria/store';

export const SpinnerCont = styled.div`
    display: flex;
    justify-content: center;
`;

const criteriaStore = createCriteriaStore({});

const AddEditContextWrapper = ({ criteria, ...props }) => {
    const store = React.useRef(criteriaStore).current;

    return (
        <CriteriaStoreContext.Provider value={{ store, sessionToken: props.sessionToken }}>
            <CriteriaStoreInit criteria={criteria} {...props}></CriteriaStoreInit>
        </CriteriaStoreContext.Provider>
    );
};

const CriteriaStoreInit = ({ criteria, ...props }) => {
    const init = useCriteriaStoreInit();

    React.useEffect(() => {
        init(criteria);
    }, [criteria]);

    return <AddEdit criteriaUuid={criteria?.uuid} {...props} />;
};

export const AddEditWrapper = ({ sessionToken, selectedCriteriaId, ...props }) => {
    const criteriaQuery = useGetCriteriaById({
        sessionToken: sessionToken,
        criteriaId: selectedCriteriaId
    });
    const isNew = React.useMemo(() => selectedCriteriaId === 'new', [selectedCriteriaId]);
    const [criteria, setCriteria] = React.useState(null);

    React.useEffect(() => {
        if (!isNew) {
            if (criteriaQuery?.isLoading) {
                setCriteria({});
                return;
            }
            if (typeof criteriaQuery?.data === 'object') {
                setCriteria(criteriaQuery?.data);
            } else {
                // This is an error
                alert('Subject data not found, please refresh the page');
                setCriteria({});
            }
            return;
        } else {
            setCriteria(typeof uiConfig.newDoc === 'object' ? uiConfig.newDoc : {});
        }
    }, [isNew, selectedCriteriaId, criteriaQuery?.isLoading, criteriaQuery?.data]);

    return (criteriaQuery?.isLoading && !isNew) || criteria === null || Object.keys(criteria).length === 0 ? (
        <SpinnerCont>
            <Spinner size={'2.3rem'} color={'#1E527E'} />
        </SpinnerCont>
    ) : (
        <AddEditContextWrapper
            {...props}
            sessionToken={sessionToken}
            criteria={criteria}
            selectedCriteriaId={selectedCriteriaId}
        />
    );
};

const AddEdit = ({
    formModel,
    selectedCriteriaId,
    criteriaUuid,
    closeDialog,
    sessionToken,
    saveDisabled,
    onMount,
    isExistingDoc,
    setEditingPredicate,
    allCriteria
}) => {
    const [criteriaId, setCriteriaId] = React.useState(selectedCriteriaId);
    const [criteriaType, setCriteriaType] = React.useState('');
    const isNew = React.useMemo(() => criteriaId === 'new', [criteriaId]);
    const [wasSuccessful, setWasSuccessful] = React.useState(false);
    const [buttonClicked, setButtonClicked] = React.useState();
    const buildDTO = useCriteriaStoreBuildDTO();
    const resetStore = useCriteriaStoreReset();
    const CloseDialog = () => {
        if (typeof closeDialog === 'function') {
            closeDialog();
        }
        resetStore();
    };
    const onClose = () => {
        if (edited) {
            setShowDiscardChangesModal(true);
        } else {
            CloseDialog();
        }
    };
    const [showDiscardChangesModal, setShowDiscardChangesModal] = React.useState(false);
    const mutationConfig = {
        sessionToken,
        onErrorCb: (e) => {
            setFatalError(e);
            setWasSuccessful(false);
        },
        onSettledCb: () => {
            setButtonClicked();
        }
    };
    const createCriteria = useCreateCriteria({
        ...mutationConfig,
        onSuccessCb: ({ data, context = {} }) => {
            setFatalError(null);
            setWasSuccessful(true);
            setEdited(false);
            setCriteriaId(data?.criteriaId);
            if (context?.closeOnSuccess) {
                setTimeout(() => {
                    CloseDialog();
                }, 500);
            } else {
                setEditingPredicate(data?.criteriaId);
            }
        }
    });
    const updateCriteria = useSaveCriteria({
        ...mutationConfig,
        criteriaId,
        onSuccessCb: ({ context = {} }) => {
            setFatalError(null);
            setWasSuccessful(true);
            setEdited(false);
            if (context?.closeOnSuccess) {
                setTimeout(() => {
                    CloseDialog();
                }, 500);
            }
        }
    });
    const criteriaMetadataQuery = useGetCriteriaMetadata(sessionToken);
    const [compose, updateCompose] = React.useState(false);
    const [edited, setEdited] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [fatalError, setFatalError] = React.useState(null);
    const criteria = useCriteriaStorePredicate(criteriaUuid);
    const criteriaState = React.useMemo(() => {
        if (isNew) {
            return { ...uiConfig.newDoc };
        } else {
            return criteria;
        }
    }, [criteria, isNew]);

    React.useEffect(() => {
        if (edited && wasSuccessful) {
            setWasSuccessful(false);
        }
    }, [edited]);

    const prepareDocument = (docState, newCriteria) => {
        const formattedValues = {};

        if (typeof docState?.criteriaTypeDTO !== 'undefined') {
            formattedValues.criteriaTypeDTO = (criteriaMetadataQuery?.data?.options?.criteriaType ?? []).find(
                ({ criteriaTypeId }) => criteriaTypeId === criteriaType
            );
        }

        return {
            ...docState,
            ...formattedValues,
            dtoState: newCriteria ? dtoState.New : dtoState.Updated
        };
    };

    const onApplyFormState = (formState) => {
        if (saveDisabled) {
            console.warn('Authoring Interface is currently read-only.');
            return;
        }
        let update = prepareDocument(
            {
                ...(criteriaState ?? {}),
                ...formState
            },
            isNew
        );
        updateCompose(false);
        let { valid } = validate(update);
        if (!valid) return;
        if (isExistingDoc(update)) {
            const alreadyExistsMessage = 'This criteria already exists in the system.';
            Messenger.showMessage({
                type: `error`,
                title: alreadyExistsMessage
            });
            setFatalError(alreadyExistsMessage);
            return;
        }
        const payload = buildDTO(update);

        if (isNew) {
            createCriteria.mutate({
                newCriteria: update,
                context: { closeOnSuccess: buttonClicked === 'save' }
            });
        } else {
            updateCriteria.mutate({ update: payload, context: { closeOnSuccess: buttonClicked === 'save' } });
        }
    };

    const isLoading = React.useMemo(() => createCriteria.isLoading || updateCriteria.isLoading, [
        createCriteria,
        updateCriteria
    ]);

    // Temporary validation solution
    const validate = (formState) => {
        let errors = {};
        for (let field of formModel) {
            let { valid, failed } = field.validation(formState[field.accessor]);
            if (!valid) {
                errors[field.accessor] = 'Invalid Input: ' + failed.join(', ');
            }
            // Add additional validation here;
        }
        // Add relational validation here

        setErrors(errors);
        let valid = Object.keys(errors).length === 0;
        return { valid, failureMsg: '' };
    };

    const prepareOptions = () => {
        let optionsOverride = {};
        optionsOverride.criteriaTypeDTO = criteriaMetadataQuery?.data?.enums?.criteriaType;
        optionsOverride.method = criteriaMetadataQuery?.data?.enums?.method;
        return optionsOverride;
    };

    let optionsOverride = prepareOptions();

    for (let key in optionsOverride) {
        let subIndex = formModel.findIndex((v) => v.accessor === key);
        if (subIndex !== -1) {
            formModel[subIndex].options = optionsOverride[key];
        }
    }

    return (
        <>
            <div>
                <Dialog
                    style={{ height: '90vh', width: '90vw', maxWidth: '100rem', maxHeight: '100rem' }}
                    close={onClose}
                    size="lg"
                    visible
                    id="criteria-manager-addEdit"
                >
                    <DialogHeader close={onClose}>
                        {isNew ? 'Add Criteria' : `Edit ${criteriaState?.name}`}
                    </DialogHeader>
                    <DialogBody style={{ overflowX: 'hidden', flex: '1', maxHeight: 'none' }}>
                        <div>
                            {fatalError != null && (
                                <FeedbackCard
                                    intent={'danger'}
                                    feedback={{
                                        heading: `An error has occurred.`,
                                        message:
                                            typeof fatalError === 'string'
                                                ? fatalError
                                                : `Please wait a few moments before trying your request
again.`
                                    }}
                                    details={fatalError}
                                />
                            )}
                            <TabbedContainer
                                expandHeaderX
                                // activeBackground={'#e7f5ff'}
                                tabs={{
                                    details: {
                                        label: 'Details',
                                        alwaysMounted: true,
                                        children: (
                                            <>
                                                {isNew ? 'Enter' : 'Adjust'} required information below.
                                                <p style={{ margin: `1.33rem 0 0 0` }}></p>
                                                {!isNew && (criteriaUuid === null || !criteriaState) ? (
                                                    <SpinnerCont>
                                                        <Spinner size={'2.3rem'} color={'#1E527E'} />
                                                    </SpinnerCont>
                                                ) : (
                                                    <Form
                                                        formModel={formModel}
                                                        data={criteriaState}
                                                        isNew={isNew}
                                                        initDataIfNew={false}
                                                        errors={errors}
                                                        compose={compose}
                                                        onApplyFormState={onApplyFormState}
                                                        setEdited={setEdited}
                                                        fatalError={fatalError}
                                                        onMount={onMount}
                                                        formFieldBasis={[15, 85]}
                                                        hookFxns={{
                                                            updateCriteriaType: setCriteriaType
                                                        }}
                                                    />
                                                )}
                                            </>
                                        )
                                    },
                                    items: {
                                        label: 'Rule',
                                        disabled: isNew,
                                        children: (
                                            <RuleEditor
                                                criteriaUuid={criteriaUuid}
                                                criteriaId={criteriaState?.criteriaId}
                                                sessionToken={sessionToken}
                                                setEdited={setEdited}
                                            />
                                        )
                                    }
                                }}
                            />
                        </div>
                    </DialogBody>
                    <DialogFooter>
                        {!isLoading && wasSuccessful && (
                            <div style={{ marginRight: '0.5rem' }}>
                                <MaterialIcon icon={'done_outline'} color={'#04846D'} />
                            </div>
                        )}
                        <Button
                            text
                            appearance="primary"
                            disabled={!edited || saveDisabled}
                            onClick={() => {
                                setButtonClicked('save');
                                updateCompose(true);
                            }}
                            loading={isLoading && buttonClicked === 'save'}
                        >
                            Save Criteria
                        </Button>
                        <Button
                            text
                            appearance="secondary"
                            disabled={!edited || saveDisabled}
                            onClick={() => {
                                setButtonClicked('apply');
                                updateCompose(true);
                            }}
                            loading={isLoading && buttonClicked === 'apply'}
                        >
                            Apply
                        </Button>
                        <Button text appearance="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                    </DialogFooter>
                </Dialog>
            </div>
            {showDiscardChangesModal ? (
                <ConfirmationModal
                    header={`Discard unsaved changes?`}
                    body={
                        'You have unsaved changes which will be discarded upon closing this modal. Do you wish to proceed?'
                    }
                    confirmButtonText={'Yes, Discard Changes'}
                    confirmButtonProps={{ appearance: 'destructive', text: true }}
                    closeDialog={() => setShowDiscardChangesModal(false)}
                    onConfirm={() =>
                        new Promise((resolve) => {
                            CloseDialog();
                            resolve();
                        })
                    }
                />
            ) : null}
        </>
    );
};

export default AddEdit;

AddEdit.propTypes = {
    selectedCriteriaId: PropTypes.string,
    formModel: PropTypes.arrayOf(PropTypes.object),
    allCriteria: PropTypes.arrayOf(PropTypes.object),
    isNew: PropTypes.bool,
    closeDialog: PropTypes.func,
    isExistingDoc: PropTypes.func,
    onMount: PropTypes.func,
    sessionToken: PropTypes.string,
    saveDisabled: PropTypes.bool
};
